import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { QrCode, ScanLine } from "lucide-react";
import { NeuButton, NeuCard, NeuInput } from "../../components/neu/Neu";

export const InviteGate = ({ token, setToken, verify, busy, error }) => {
  const { t } = useTranslation();
  const videoRef = useRef(null);
  const [scanning, setScanning] = useState(false);
  const supported = typeof window !== "undefined" && "BarcodeDetector" in window;

  useEffect(() => {
    if (!scanning || !supported) return undefined;
    let stream;
    let timer;
    let stopped = false;
    (async () => {
      // eslint-disable-next-line no-undef
      const detector = new window.BarcodeDetector({ formats: ["qr_code"] });
      stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: "environment" } });
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play().catch(() => {});
      }
      timer = setInterval(async () => {
        if (stopped || !videoRef.current) return;
        const codes = await detector.detect(videoRef.current).catch(() => []);
        const value = codes[0]?.rawValue;
        if (value) {
          const code = value.split("/join/").pop();
          setScanning(false);
          verify(code);
        }
      }, 700);
    })();
    return () => {
      stopped = true;
      clearInterval(timer);
      stream?.getTracks().forEach((tr) => tr.stop());
    };
  }, [scanning, supported, verify]);

  return (
    <NeuCard className="animate-rise-in" data-testid="invite-gate">
      <p className="label-xs">{t("join.tokenLabel")}</p>
      <NeuInput
        className="mt-3"
        data-testid="join-token"
        value={token}
        onChange={(e) => setToken(e.target.value)}
        placeholder="XXXXXXXX"
      />
      {error ? (
        <p className="mt-3 text-sm font-semibold text-destructive" data-testid="join-error">
          {error}
        </p>
      ) : null}
      <div className="mt-4 flex flex-wrap gap-2">
        <NeuButton variant="primary" data-testid="join-verify" disabled={busy} onClick={() => verify()}>
          <span className="flex items-center gap-2">
            <QrCode className="h-4 w-4" /> {t("join.verify")}
          </span>
        </NeuButton>
        {supported ? (
          <NeuButton data-testid="join-scan" onClick={() => setScanning((s) => !s)}>
            <span className="flex items-center gap-2">
              <ScanLine className="h-4 w-4" /> {scanning ? t("join.stopScan") : t("join.scan")}
            </span>
          </NeuButton>
        ) : null}
      </div>
      {scanning ? (
        <video ref={videoRef} muted playsInline className="mt-4 h-56 w-full rounded-2xl object-cover shadow-neu-in" />
      ) : null}
    </NeuCard>
  );
};
